import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Collapse,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  Article as ArticleIcon,
  Code as PromptIcon,
} from '@mui/icons-material';
import { generateSectionContent, previewAIPrompt } from 'shared/utils/proposalApi';
import CreditBalance from 'components/common/billing/CreditBalance';
import PaymentDialog from 'components/common/billing/PaymentDialog';
import useBillingBalance from 'components/common/billing/useBillingBalance';
import { useUserContext } from 'shared/contexts/UserContext';
import { axiosService } from 'shared/helpers/axios';
import * as byokVault from 'shared/utils/byokVault';
import OwnKeyPanel from './OwnKeyPanel';

const SOURCE_KEY = 'otable.ai.source';
const readSource = () => { try { return localStorage.getItem(SOURCE_KEY) || 'credits'; } catch { return 'credits'; } };

// Rough in-browser token estimate: ~4 chars per token.
const estimateTokens = (text) => Math.max(0, Math.round((text || '').length / 4));

const AISectionWriter = ({ section, proposal, onUseContent }) => {
  const [instruction, setInstruction]   = useState('');
  const [generated, setGenerated]       = useState('');
  const [generating, setGenerating]     = useState(false);
  const [error, setError]               = useState(null);
  const [contextOpen, setContextOpen]   = useState(false);
  const [paymentOpen, setPaymentOpen]   = useState(false);
  const [lastCost, setLastCost]         = useState(null);

  // Admin prompt preview state
  const [promptOpen, setPromptOpen]         = useState(false);
  const [promptData, setPromptData]         = useState(null);  // backend preview response
  const [promptLoading, setPromptLoading]   = useState(false);
  const [promptStale, setPromptStale]       = useState(false); // instruction changed since last fetch
  const debounceRef = useRef(null);

  const { balance, isUnlimited, refresh: refreshBalance } = useBillingBalance();
  const { user } = useUserContext();
  const isAdmin = user?.is_superuser || user?.is_staff || false;

  // 'credits' (Otable generates, 1 credit) or 'own' (the user's own API key, called from the browser)
  const [source, setSource] = useState(readSource);
  const [, setVaultVersion] = useState(0); // re-render after vault status changes
  const ownKey = source === 'own';
  const vaultStatus = user ? byokVault.status(user.id) : { configured: false, unlocked: false };
  const generationBlocked = ownKey ? !vaultStatus.unlocked : (!isAdmin && balance === 0);
  const changeSource = (value) => {
    if (!value) return;
    setSource(value);
    try { localStorage.setItem(SOURCE_KEY, value); } catch { /* ignore */ }
  };

  const members   = proposal?.consortium_members || [];
  const callTitle = proposal?.call?.title || proposal?.call_title || '';
  const wordLimit = section?.word_limit;
  const language  = proposal?.language || 'en';

  // Fetch prompt preview from backend
  const fetchPreview = useCallback(async (instr) => {
    if (!section?.id) return;
    setPromptLoading(true);
    setPromptStale(false);
    const result = await previewAIPrompt(section.id, instr);
    if (result.success) {
      setPromptData(result.data);
    }
    setPromptLoading(false);
  }, [section?.id]);

  // When prompt panel opens, fetch immediately
  useEffect(() => {
    if (promptOpen && isAdmin && !promptData) {
      fetchPreview(instruction);
    }
  }, [promptOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // When instruction changes after first preview load, debounce re-fetch
  useEffect(() => {
    if (!promptOpen || !isAdmin || !promptData) return;
    setPromptStale(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPreview(instruction), 1200);
    return () => clearTimeout(debounceRef.current);
  }, [instruction]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset preview when section changes
  useEffect(() => {
    setPromptData(null);
    setPromptStale(false);
  }, [section?.id]);

  // Own key: Otable only assembles the prompt; the browser calls the provider directly
  const handleGenerateOwnKey = async () => {
    setGenerating(true);
    setError(null);
    setGenerated('');
    setLastCost(null);
    try {
      const { data } = await axiosService.post(`/proposal/sections/${section.id}/ai_byok_prompt/`, { instruction });
      let streamed = '';
      const result = await byokVault.generate(
        user.id,
        { system: data.system_prompt, user: data.user_prompt },
        (chunk) => { streamed += chunk; setGenerated(streamed); },
      );
      setGenerated(result.text);
      if (result.truncated) setError('The response hit the length limit and may be cut off.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    setGenerating(false);
  };

  const handleGenerate = async () => {
    if (ownKey) { await handleGenerateOwnKey(); return; }
    if (!isAdmin && balance === 0) {
      setPaymentOpen(true);
      return;
    }
    setGenerating(true);
    setError(null);
    setGenerated('');
    setLastCost(null);

    const result = await generateSectionContent(section.id, instruction);
    if (result.success) {
      setGenerated(result.data.result);
      setLastCost({
        credits: result.data.credits_used ?? 1,
        usd:     result.data.cost_usd  ?? null,
        tokens:  result.data.tokens_used ?? null,
      });
      refreshBalance();
    } else {
      if (result.error?.toLowerCase().includes('credit') || result.error?.toLowerCase().includes('insufficient')) {
        setPaymentOpen(true);
      } else {
        setError(result.error);
      }
    }
    setGenerating(false);
  };

  const handleUse = () => {
    onUseContent?.(generated);
    setGenerated('');
    setInstruction('');
  };

  const handleDiscard = () => setGenerated('');

  const instructionTokens = estimateTokens(instruction);

  return (
    <>
      <Paper
        variant="outlined"
        sx={{ p: 2, borderColor: 'primary.light', bgcolor: 'primary.50', borderRadius: 2 }}
      >
        {/* Header row */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={700} color="primary.main">
              AI Writing Assistant
            </Typography>
            {wordLimit && (
              <Chip label={`~${wordLimit} words`} size="small" variant="outlined" color="primary" sx={{ height: 20, fontSize: 11 }} />
            )}
            {language !== 'en' && (
              <Chip
                label={proposal?.language_name || language.toUpperCase()}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.5}>
            {!ownKey && <CreditBalance balance={balance} isUnlimited={isAdmin || isUnlimited} onClick={() => setPaymentOpen(true)} lowThreshold={5} />}
            {isAdmin && (
              <Tooltip title={promptOpen ? 'Hide prompt preview' : 'Show prompt sent to AI'}>
                <IconButton size="small" onClick={() => setPromptOpen(v => !v)} color={promptOpen ? 'primary' : 'default'}>
                  <PromptIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={contextOpen ? 'Hide context' : 'Show what AI will use'}>
              <IconButton size="small" onClick={() => setContextOpen(v => !v)}>
                {contextOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Context preview */}
        <Collapse in={contextOpen}>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'background.paper' }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" gutterBottom>
              Context the AI will use
            </Typography>
            {callTitle && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                <ArticleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">Call: {callTitle}</Typography>
              </Stack>
            )}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <PeopleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {members.length} consortium member{members.length !== 1 ? 's' : ''} (skills, interests, publications)
              </Typography>
            </Stack>
            {members.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                {members.slice(0, 8).map(m => (
                  <Chip key={m.id} label={m.name || m.username || m.email} size="small" sx={{ height: 18, fontSize: 10 }} />
                ))}
                {members.length > 8 && (
                  <Chip label={`+${members.length - 8} more`} size="small" sx={{ height: 18, fontSize: 10 }} variant="outlined" />
                )}
              </Stack>
            )}
          </Paper>
        </Collapse>

        {/* Credits or own key */}
        <ToggleButtonGroup size="small" exclusive value={source} onChange={(e, v) => changeSource(v)} sx={{ mb: 1.5 }}>
          <ToggleButton value="credits" sx={{ textTransform: 'none' }}>Otable credits</ToggleButton>
          <ToggleButton value="own" sx={{ textTransform: 'none' }}>My own API key</ToggleButton>
        </ToggleButtonGroup>
        {ownKey && user && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
            <OwnKeyPanel userId={user.id} onChange={() => setVaultVersion(v => v + 1)} />
          </Paper>
        )}

        {/* Instruction input */}
        <TextField
          multiline minRows={2} maxRows={4} fullWidth size="small"
          value={instruction}
          onChange={e => setInstruction(e.target.value.slice(0, 8000))}
          placeholder={`Optional: guide the AI — e.g. "focus on machine learning methods", "emphasise clinical impact"`}
          helperText={
            instruction.length > 0
              ? `${instruction.length}/8000 characters (~${estimateTokens(instruction)} tokens)${instruction.length >= 7200 ? ' — approaching limit' : ''}`
              : 'Max 2 000 tokens (~8 000 characters)'
          }
          slotProps={{
            htmlInput: { maxLength: 8000 },
            formHelperText: { sx: { color: instruction.length >= 7200 ? 'warning.main' : 'text.secondary' } },
          }}
          sx={{ mb: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}
        />

        {/* No-credits nudge */}
        {!ownKey && !isAdmin && balance === 1 && !generated && !lastCost && (
          <Alert severity="info" sx={{ mb: 1.5 }} icon={false}>
            You have <strong>1 free credit</strong> — use it to generate a section draft.
          </Alert>
        )}
        {!ownKey && !isAdmin && balance === 0 && !generated && (
          <Alert severity="warning" sx={{ mb: 1.5 }}
            action={<Button size="small" onClick={() => setPaymentOpen(true)}>Add Credits</Button>}
          >
            No credits remaining.
          </Alert>
        )}

        {/* Cost receipt */}
        {lastCost && (
          <Alert severity="success" sx={{ mb: 1.5 }} icon={false}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" fontWeight={600}>
                {isAdmin ? 'Admin — no charge' : `${lastCost.credits} credit${lastCost.credits !== 1 ? 's' : ''} used`}
              </Typography>
              {lastCost.usd != null && (
                <Typography variant="caption" color="text.secondary">${lastCost.usd.toFixed(4)} OpenAI cost</Typography>
              )}
              {lastCost.tokens != null && (
                <Typography variant="caption" color="text.secondary">{lastCost.tokens.toLocaleString()} tokens</Typography>
              )}
            </Stack>
          </Alert>
        )}

        {/* Generate button */}
        {!generated && (
          <Button
            variant="contained" fullWidth
            onClick={handleGenerate}
            disabled={generating || generationBlocked}
            startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
          >
            {generating
              ? 'Generating…'
              : ownKey && !vaultStatus.unlocked
              ? 'Unlock your API key to generate'
              : !ownKey && !isAdmin && balance === 0
              ? 'Purchase credits to generate'
              : `Generate "${section?.title || 'Section'}" Draft`}
          </Button>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        {/* Generated result */}
        {generated && (
          <Box sx={{ mt: 1.5 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 1 }}>
              Generated draft — review before using
            </Typography>
            <Paper variant="outlined" sx={{
              p: 2, maxHeight: 320, overflowY: 'auto', bgcolor: 'background.paper',
              borderRadius: 1, mb: 1.5, whiteSpace: 'pre-wrap',
              fontFamily: 'inherit', fontSize: 14, lineHeight: 1.7,
            }}>
              {generated}
            </Paper>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={handleUse} sx={{ flex: 1 }}>
                Use This
              </Button>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleGenerate}
                disabled={generating || generationBlocked} sx={{ flex: 1 }}>
                Regenerate
              </Button>
              <Tooltip title="Discard">
                <IconButton onClick={handleDiscard} size="small"><CloseIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Stack>
          </Box>
        )}

        {/* ── Admin: Prompt Preview ─────────────────────────────────────── */}
        {isAdmin && (
          <Collapse in={promptOpen}>
            <Divider sx={{ mt: 2, mb: 1.5 }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Prompt Preview (admin only)
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                {promptLoading && <CircularProgress size={14} />}
                {promptStale && !promptLoading && (
                  <Chip label="stale" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                )}
                {promptData && !promptLoading && (
                  <Stack direction="row" spacing={0.5}>
                    <Chip
                      label={`sys ~${promptData.system_tokens.toLocaleString()} tok`}
                      size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }}
                    />
                    <Chip
                      label={`usr ~${promptData.user_tokens.toLocaleString()} tok`}
                      size="small" variant="outlined" color="primary" sx={{ height: 18, fontSize: 10 }}
                    />
                    <Chip
                      label={`total ~${promptData.total_tokens.toLocaleString()} tok`}
                      size="small" color="secondary" sx={{ height: 18, fontSize: 10 }}
                    />
                  </Stack>
                )}
                {!promptData && !promptLoading && (
                  <Typography variant="caption" color="text.secondary">
                    instruction ~{instructionTokens} tok
                  </Typography>
                )}
                <Tooltip title="Refresh preview">
                  <IconButton size="small" onClick={() => fetchPreview(instruction)} disabled={promptLoading}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            {promptData && (
              <>
                {promptData.language !== 'en' && (
                  <Alert severity="info" sx={{ mb: 1 }} icon={false}>
                    <Typography variant="caption">
                      Output language: <strong>{promptData.language_name}</strong> — language instruction appended to user prompt.
                    </Typography>
                  </Alert>
                )}

                {/* System prompt */}
                <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  System prompt ({promptData.system_tokens.toLocaleString()} tokens)
                </Typography>
                <Paper variant="outlined" sx={{
                  p: 1.5, mb: 1.5, maxHeight: 160, overflowY: 'auto',
                  bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: 11,
                  whiteSpace: 'pre-wrap', lineHeight: 1.5,
                }}>
                  {promptData.system_prompt}
                </Paper>

                {/* User prompt */}
                <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  User prompt ({promptData.user_tokens.toLocaleString()} tokens)
                </Typography>
                <Paper variant="outlined" sx={{
                  p: 1.5, maxHeight: 300, overflowY: 'auto',
                  bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: 11,
                  whiteSpace: 'pre-wrap', lineHeight: 1.5,
                }}>
                  {promptData.user_prompt}
                </Paper>
              </>
            )}
          </Collapse>
        )}
      </Paper>

      <PaymentDialog
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        productName="AI Writing Credits"
        productDescription="Credits cover GPT-4o token costs. A typical section draft uses 1 credit (~$0.02 of OpenAI usage)."
        unitLabel="credits"
        costPerOperation={1}
        operationLabel="section draft"
        currentBalance={balance ?? 0}
        onSuccess={() => { refreshBalance(); setPaymentOpen(false); }}
      />
    </>
  );
};

export default AISectionWriter;
